import React, { FC, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  useAnalytics,
  useFlowManager,
  useSignupSession,
  useShippingAddress,
  useHasBilling,
  useBillingInfo
} from '../../../hooks'
import { screenPathHelper } from '../../../lib/analyticsClients/helpers/cdm/parametersHelpers'
import { StepNames } from '../../../types'

type AnalyticsEventHandlerProps = {
  children: React.ReactNode
}

export const AnalyticsEventHandler: FC<AnalyticsEventHandlerProps> = ({
  children
}) => {
  const { pathname } = useLocation()
  const flowManager = useFlowManager()
  const {
    partialSubscriptionId,
    partialSubscriptionFlowName,
    requireBilling
  } = useSignupSession()
  const { publishDisplayEvent, publishClickEvent } = useAnalytics()
  const shippingAddressState = useShippingAddress().data
  const hasShipping = !!shippingAddressState?.street1
  const hasBilling = useHasBilling()
  const billingInfoState = useBillingInfo().data
  const billingAndShippingLoaded = !!(shippingAddressState && billingInfoState)
  const step = flowManager.stepFromPath(pathname)
  const displayEvent = flowManager.trackDisplayEvent(step)
  const { ENROLL_STEP } = StepNames

  // Generate the screen URL based on the current step and flow
  const getScreenUrl = useCallback(() => {
    const stepComponent = flowManager.getStepComponentName(pathname)
    const screenPath = screenPathHelper(partialSubscriptionFlowName, step)
    return `${screenPath}${stepComponent}`
  }, [flowManager, pathname, partialSubscriptionFlowName, step])

  // Generate the billing and shipping control detail string
  // based on the current state of the shipping and billing information
  const getBillingAndShippingControlDetail = useCallback(() => {
    if (!billingAndShippingLoaded) return
    const billingAndShippingControlDetail = `hasShipping=${hasShipping}&hasBilling=${hasBilling}&billingRequired=${!!requireBilling}`
    return billingAndShippingControlDetail
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAndShippingLoaded])

  useEffect(() => {
    if (partialSubscriptionId && displayEvent && step !== ENROLL_STEP) {
      publishDisplayEvent({
        pathname,
        screenUrl: getScreenUrl()
      })
    }
  }, [
    partialSubscriptionId,
    displayEvent,
    step,
    ENROLL_STEP,
    pathname,
    getScreenUrl,
    publishDisplayEvent
  ])

  useEffect(() => {
    if (partialSubscriptionId && displayEvent && step == ENROLL_STEP) {
      if (!billingAndShippingLoaded) {
        return
      }

      const enrollStepControlDetail = getBillingAndShippingControlDetail()
      publishDisplayEvent({
        pathname,
        screenUrl: getScreenUrl(),
        controlDetail: enrollStepControlDetail,
        controlName: 'EnrollStepDisplayed'
      })
    }
  }, [
    partialSubscriptionId,
    displayEvent,
    step,
    ENROLL_STEP,
    billingAndShippingLoaded,
    pathname,
    getScreenUrl,
    getBillingAndShippingControlDetail,
    publishDisplayEvent
  ])

  return (
    <div
      onClick={(event) => {
        publishClickEvent({ eventTarget: event?.target })
      }}
    >
      {children}
    </div>
  )
}
